/**
 * 管理员账号管理相关 Store
 * 负责管理员端账号数据的加载、审核、管理和操作
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCacheStore } from '../cache'
import { adminAccountsApi } from '@/https'
import {
    AccountStatusEnum
} from '@/https'
import type {
    AdminGameAccountDetail,
    AdminAccountListRequest,
    AuditStatistics
} from '@/https/types_admin'

export const useAdminAccountsStore = defineStore('admin-accounts', () => {
    const cacheStore = useCacheStore()

    // ===== 状态管理 =====
    const adminAccountList = ref<AdminGameAccountDetail[]>([])
    const currentAccountDetail = ref<AdminGameAccountDetail | null>(null)
    const accountStatistics = ref<AuditStatistics | null>(null)

    // 分页信息
    const accountPagination = ref({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    // 查询参数
    const lastAccountQuery = ref<AdminAccountListRequest>({})

    // 加载状态
    const accountListLoading = ref(false)
    const accountDetailLoading = ref(false)
    const accountStatisticsLoading = ref(false)
    const accountOperationLoading = ref(false)

    // 错误状态
    const accountListError = ref<string | null>(null)
    const accountDetailError = ref<string | null>(null)
    const accountStatisticsError = ref<string | null>(null)
    const accountOperationError = ref<string | null>(null)

    // ===== 计算属性 =====
    const accountListByStatus = computed(() => {
        const groups: Record<string, AdminGameAccountDetail[]> = {}
        adminAccountList.value.forEach(account => {
            const status = account.status || 'unknown'
            if (!groups[status]) {
                groups[status] = []
            }
            groups[status].push(account)
        })
        return groups
    })

    const pendingAccountsCount = computed(() => {
        return adminAccountList.value.filter(account => account.status === AccountStatusEnum.PENDING).length
    })

    const hasMoreAccounts = computed(() => accountPagination.value.has_more)

    // ===== 数据加载方法 =====

    /**
     * 加载管理员账号列表
     */
    const loadAdminAccountList = async (
        params?: AdminAccountListRequest,
        forceRefresh = false,
        replaceQuery = false
    ) => {
        const query = replaceQuery ? { ...(params || {}) } : { ...lastAccountQuery.value, ...params }
        lastAccountQuery.value = query

        try {
            accountListLoading.value = true
            accountListError.value = null

            const response = await cacheStore.smartCache(
                `admin-accounts:list:${JSON.stringify(query)}`,
                () => adminAccountsApi.getAccountList(query),
                forceRefresh
            )
            adminAccountList.value = response.items
            accountPagination.value = {
                total: response.total,
                page: response.page,
                size: response.size,
                has_more: response.has_more
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载账号列表失败'
            accountListError.value = errorMessage
            console.error('加载管理员账号列表失败:', error)
        } finally {
            accountListLoading.value = false
        }
    }




    /**
     * 加载账号详情
     */
    const loadAdminAccountDetail = async (id: number, forceRefresh = false) => {
        try {
            accountDetailLoading.value = true
            accountDetailError.value = null

            const response = await cacheStore.smartCache(
                `admin-accounts:detail:${id}`,
                () => adminAccountsApi.getAccountDetail(id),
                forceRefresh
            )
            currentAccountDetail.value = response
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载账号详情失败'
            accountDetailError.value = errorMessage
            console.error(`加载账号详情失败 (ID: ${id}):`, error)
        } finally {
            accountDetailLoading.value = false
        }
    }

    /**
     * 加载账号统计数据
     */
    const loadAccountStatistics = async (forceRefresh = false) => {
        try {
            accountStatisticsLoading.value = true
            accountStatisticsError.value = null

            const response = await cacheStore.smartCache(
                'admin-accounts:statistics',
                () => adminAccountsApi.getAccountStatistics(),
                forceRefresh
            )
            accountStatistics.value = response
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载账号统计数据失败'
            accountStatisticsError.value = errorMessage
            console.error('加载账号统计数据失败:', error)
        } finally {
            accountStatisticsLoading.value = false
        }
    }

    // ===== 业务操作方法 =====

    /**
     * 审核通过账号
     */
    const approveAccount = async (id: number) => {
        try {
            accountOperationLoading.value = true
            accountOperationError.value = null
            await adminAccountsApi.approveAccount(id)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-accounts:list')
            cacheStore.removeCacheByPrefix(`admin-accounts:detail:${id}`)
            cacheStore.removeCacheByPrefix('admin-accounts:statistics')
            await loadAdminAccountDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '审核通过失败'
            accountOperationError.value = errorMessage
            console.error(`审核通过失败 (ID: ${id}):`, error)
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 审核拒绝账号
     */
    const rejectAccount = async (id: number, reason: string) => {
        try {
            accountOperationLoading.value = true
            accountOperationError.value = null
            await adminAccountsApi.rejectAccount(id, { reason })
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-accounts:list')
            cacheStore.removeCacheByPrefix(`admin-accounts:detail:${id}`)
            cacheStore.removeCacheByPrefix('admin-accounts:statistics')
            await loadAdminAccountDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '审核拒绝失败'
            accountOperationError.value = errorMessage
            console.error(`审核拒绝失败 (ID: ${id}):`, error)
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 强制删除账号
     */
    const forceDeleteAccount = async (id: number) => {
        try {
            accountOperationLoading.value = true
            accountOperationError.value = null
            await adminAccountsApi.forceDeleteAccount(id)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-accounts:list')
            cacheStore.removeCacheByPrefix(`admin-accounts:detail:${id}`)
            cacheStore.removeCacheByPrefix('admin-accounts:statistics')
            await loadAdminAccountList({}, true) // 重新加载列表
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '强制删除账号失败'
            accountOperationError.value = errorMessage
            console.error(`强制删除账号失败 (ID: ${id}):`, error)
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 封禁账号
     */
    const banAccount = async (id: number) => {
        try {
            accountOperationLoading.value = true
            accountOperationError.value = null
            await adminAccountsApi.banAccount(id)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-accounts:list')
            cacheStore.removeCacheByPrefix(`admin-accounts:detail:${id}`)
            cacheStore.removeCacheByPrefix('admin-accounts:statistics')
            await loadAdminAccountDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '封禁账号失败'
            accountOperationError.value = errorMessage
            console.error(`封禁账号失败 (ID: ${id}):`, error)
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 解封账号
     */
    const unbanAccount = async (id: number) => {
        try {
            accountOperationLoading.value = true
            accountOperationError.value = null
            await adminAccountsApi.unbanAccount(id)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-accounts:list')
            cacheStore.removeCacheByPrefix(`admin-accounts:detail:${id}`)
            cacheStore.removeCacheByPrefix('admin-accounts:statistics')
            await loadAdminAccountDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '解封账号失败'
            accountOperationError.value = errorMessage
            console.error(`解封账号失败 (ID: ${id}):`, error)
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 恢复账号
     */
    const restoreAccount = async (id: number) => {
        try {
            accountOperationLoading.value = true
            accountOperationError.value = null
            await adminAccountsApi.restoreAccount(id)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-accounts:list')
            cacheStore.removeCacheByPrefix(`admin-accounts:detail:${id}`)
            cacheStore.removeCacheByPrefix('admin-accounts:statistics')
            await loadAdminAccountDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '恢复账号失败'
            accountOperationError.value = errorMessage
            console.error(`恢复账号失败 (ID: ${id}):`, error)
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 下架账号（管理员）
     */
    const unpublishAccount = async (id: number) => {
        try {
            accountOperationLoading.value = true
            accountOperationError.value = null
            await adminAccountsApi.unpublishAccount(id)
            // 清除相关缓存并刷新（保留当前筛选与分页参数）
            cacheStore.removeCacheByPrefix('admin-accounts:list')
            cacheStore.removeCacheByPrefix(`admin-accounts:detail:${id}`)
            cacheStore.removeCacheByPrefix('admin-accounts:statistics')
            await loadAdminAccountList(lastAccountQuery.value, true)
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '下架账号失败'
            accountOperationError.value = errorMessage
            console.error(`下架账号失败 (ID: ${id}):`, error)
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    // ===== 工具方法 =====

    /**
     * 根据ID查找账号
     */
    const findAccountById = (id: number) => {
        return adminAccountList.value.find(account => account.id === id)
    }

    /**
     * 清除所有状态
     */
    const clearAllStates = () => {
        adminAccountList.value = []
        currentAccountDetail.value = null
        accountStatistics.value = null
        accountPagination.value = { total: 0, page: 1, size: 20, has_more: false }
        lastAccountQuery.value = {}
        // 清除错误状态
        accountListError.value = null
        accountDetailError.value = null
        accountStatisticsError.value = null
        accountOperationError.value = null
    }

    return {
        // 状态
        adminAccountList,
        currentAccountDetail,
        accountStatistics,
        accountPagination,
        lastAccountQuery,

        // 加载状态
        accountListLoading,
        accountDetailLoading,
        accountStatisticsLoading,
        accountOperationLoading,

        // 错误状态
        accountListError,
        accountDetailError,
        accountStatisticsError,
        accountOperationError,

        // 计算属性
        accountListByStatus,
        pendingAccountsCount,
        hasMoreAccounts,

        // 数据加载方法
        loadAdminAccountList,
        loadAdminAccountDetail,
        loadAccountStatistics,

        // 业务操作方法
        approveAccount,
        rejectAccount,
        forceDeleteAccount,
        banAccount,
        unbanAccount,
        restoreAccount,
        unpublishAccount,

        // 工具方法
        findAccountById,
        clearAllStates
    }
}) 